import React from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle/toggle';
import { token } from '@atlaskit/tokens';
import AccountMenu from '@atlassian/account-menu/nav4/account-menu-nav4';

import { getConfig } from '../../../../../config';
import { useAtlassianUser } from '../../../../../hooks/use-atlassian-user';
import { ChatAside } from '../../../../components/ChatAside';
import { QueryWorkbenchTopNavigation } from '../../../../components/QueryWorkbench';
import { useAsideController } from '../../../../contexts/AsideContext';

const styles = cssMap({
	end: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	themeToggle: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

export const LabsDarkNavigation = ({
	onThemeChange,
}: {
	onThemeChange: (theme: 'light' | 'dark' | 'auto') => void;
}): React.JSX.Element => {
	const user = useAtlassianUser();
	const aside = useAsideController();
	const isCyphieOpen = aside.isOpen && aside.owner === 'twg-labs-dark';

	return (
		<QueryWorkbenchTopNavigation
			homeHref="/labs"
			homeLabel="Labs home"
			navigationLabel="Labs navigation"
			onNavigate={(path) => {
				globalThis.history.pushState(null, '', path);
				globalThis.dispatchEvent(new PopStateEvent('popstate'));
			}}
			items={[{ id: 'labs', label: 'Labs', href: '/labs', isSelected: true }]}
			endContent={
				<Box xcss={styles.end}>
					<Button
						appearance="subtle"
						iconBefore={RovoIcon}
						aria-controls="twg-labs-cyphie-panel"
						aria-expanded={isCyphieOpen}
						onClick={() =>
							aside.toggle({
								content: <ChatAside appearance="workbench" />,
								owner: 'twg-labs-dark',
							})
						}
					>
						Cyphie
					</Button>
					<Inline xcss={styles.themeToggle} alignBlock="center">
						<Text as="span">Light mode</Text>
						<Toggle
							id="labs-light-mode"
							label="Light mode"
							isChecked={false}
							onChange={() => onThemeChange('light')}
						/>
					</Inline>
					<AccountMenu
						name={user.name}
						picture={user.picture}
						email={user.email}
						viewProfileLink={`${getConfig().atlassianHomeUrl}/people/${encodeURIComponent(user.accountId)}`}
						manageProfileLink={`${getConfig().atlassianAccountUrl}/login?application=dtac&continue=${encodeURIComponent(
							`${getConfig().atlassianAccountUrl}/manage-profile`,
						)}&login_hint=${encodeURIComponent(user.email)}&prompt=none`}
						logOutLink={`${getConfig().atlassianAccountUrl}/logout?application=dtac&continue=${encodeURIComponent(
							getConfig().serviceUrl,
						)}`}
						onThemeChange={onThemeChange}
						currentTheme="dark"
					/>
				</Box>
			}
		/>
	);
};
